import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { usePolling } from "@/hooks/usePolling";
import { computeAlerts, WorkerTabKey } from "./lib/alerts";
import { KpiBand } from "./components/KpiBand";
import { AlertsBanner } from "./components/AlertsBanner";
import { OverviewTab } from "./tabs/OverviewTab";
import { DistributionTab } from "./tabs/DistributionTab";
import { OperationsTab } from "./tabs/OperationsTab";
import { getQueuesSummary, QueueSummaryResponse } from "@/services/workerApi";
import { getConnectedPlugins, PluginStatus } from "@/services/pluginStatusApi";

export default function WorkerTasksPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<QueueSummaryResponse | null>(null);
  const [plugins, setPlugins] = useState<PluginStatus[] | null>(null);
  const [lastUpdate, setLastUpdate] = useState<number | null>(null);
  const [error, setError] = useState(false);
  const [live, setLive] = useState(true);
  const [tab, setTab] = useState<WorkerTabKey>("overview");

  // Données « live » du cockpit : profondeur des files + flotte de plugins.
  // Alimente le bandeau KPI et les alertes, toujours visibles.
  const fetchLive = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        getQueuesSummary(navigate, { top: 1 }),
        getConnectedPlugins(navigate),
      ]);
      setSummary(s);
      setPlugins(p.plugins);
      setLastUpdate(Date.now());
      setError(false);
    } catch {
      // Polling non bloquant : on conserve la dernière vue connue.
      setError(true);
    }
  }, [navigate]);

  const { isHidden } = usePolling(fetchLive, 5000, { enabled: live });

  const alerts = useMemo(() => computeAlerts(summary, plugins), [summary, plugins]);

  const liveActive = live && !isHidden;
  const liveLabel = !live ? "En pause" : isHidden ? "En pause (onglet caché)" : "En direct";

  return (
    <>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex w-full items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>Supervision workers</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <div className="ml-auto flex items-center gap-3">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span
                  className={cn(
                    "h-2 w-2 rounded-full",
                    liveActive ? "bg-emerald-500 animate-pulse" : "bg-muted-foreground/40",
                  )}
                />
                {liveLabel}
              </span>
              {lastUpdate && (
                <span className={cn("text-xs", error ? "text-destructive" : "text-muted-foreground")}>
                  {error ? "Échec MAJ · " : "MAJ "}
                  {new Date(lastUpdate).toLocaleTimeString("fr-FR")}
                </span>
              )}
              <div className="flex items-center gap-1.5">
                <Switch id="live" checked={live} onCheckedChange={setLive} />
                <Label htmlFor="live" className="text-xs">Live</Label>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => void fetchLive()}
                title="Rafraîchir"
                aria-label="Rafraîchir"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </header>

        <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
          <KpiBand summary={summary} plugins={plugins} alerts={alerts} />
          <AlertsBanner alerts={alerts} onNavigate={setTab} />

          <Tabs value={tab} onValueChange={(v) => setTab(v as WorkerTabKey)}>
            <TabsList>
              <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
              <TabsTrigger value="distribution">Répartition</TabsTrigger>
              <TabsTrigger value="operations">Opérations</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-4">
              <OverviewTab
                summary={summary}
                plugins={plugins}
                navigate={navigate}
                live={live}
                onMutated={fetchLive}
              />
            </TabsContent>
            <TabsContent value="distribution" className="mt-4">
              <DistributionTab live={live} navigate={navigate} />
            </TabsContent>
            <TabsContent value="operations" className="mt-4">
              <OperationsTab navigate={navigate} />
            </TabsContent>
          </Tabs>
        </div>
      </SidebarInset>
    </>
  );
}
